import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Button,
  Divider,
  Stack,
} from '@mui/material';
import {
  Gavel as DisputeIcon,
  VerifiedUser as VerifiedIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  Notifications as BellIcon,
} from '@mui/icons-material';
import adminAlertsService, { AdminAlert, AlertCounts } from '../services/api/adminAlertsService';

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const TYPE_META: Record<AdminAlert['type'], { icon: React.ElementType; color: string; label: string }> = {
  dispute: { icon: DisputeIcon, color: '#ef4444', label: 'Dispute' },
  verification: { icon: VerifiedIcon, color: '#f59e0b', label: 'Verification' },
  payment: { icon: PaymentIcon, color: '#2563eb', label: 'Payment' },
  leakage: { icon: BellIcon, color: '#8b5cf6', label: 'Leakage' },
};

const SEVERITY_COLOR: Record<AdminAlert['severity'], 'error' | 'warning' | 'info'> = {
  high: 'error',
  medium: 'warning',
  low: 'info',
};

export default function AdminAlertsPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<AdminAlert[]>([]);
  const [counts, setCounts] = useState<AlertCounts>({ disputes: 0, verifications: 0, payments: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [alertList, alertCounts] = await Promise.all([
        adminAlertsService.getAlerts(),
        adminAlertsService.getCounts(),
      ]);
      setAlerts(alertList);
      setCounts(alertCounts);
    } catch { /* keep existing state */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Admin Alerts</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {counts.total} item{counts.total !== 1 ? 's' : ''} needing attention
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Summary chips */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Chip
          icon={<DisputeIcon sx={{ fontSize: 16 }} />}
          label={`${counts.disputes} Open Disputes`}
          onClick={() => navigate('/disputes')}
          color={counts.disputes > 0 ? 'error' : 'default'}
          variant={counts.disputes > 0 ? 'filled' : 'outlined'}
          clickable
        />
        <Chip
          icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
          label={`${counts.verifications} Pending Verifications`}
          onClick={() => navigate('/verifications')}
          color={counts.verifications > 0 ? 'warning' : 'default'}
          variant={counts.verifications > 0 ? 'filled' : 'outlined'}
          clickable
        />
        <Chip
          icon={<PaymentIcon sx={{ fontSize: 16 }} />}
          label={`${counts.payments} Pending Withdrawals`}
          onClick={() => navigate('/payments')}
          color={counts.payments > 0 ? 'info' : 'default'}
          variant={counts.payments > 0 ? 'filled' : 'outlined'}
          clickable
        />
      </Stack>

      {/* Alert List */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : alerts.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <BellIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No pending alerts</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                All disputes, verifications, and withdrawals are resolved.
              </Typography>
            </Box>
          ) : (
            alerts.map((alert, idx) => {
              const meta = TYPE_META[alert.type];
              const Icon = meta.icon;
              return (
                <React.Fragment key={alert.id}>
                  {idx > 0 && <Divider />}
                  <Box
                    onClick={() => navigate(alert.route)}
                    sx={{
                      px: 3,
                      py: 2,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: `${meta.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      <Icon sx={{ fontSize: 18, color: meta.color }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {alert.title}
                        </Typography>
                        <Chip
                          label={alert.severity}
                          color={SEVERITY_COLOR[alert.severity]}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                        {alert.description}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, mt: 0.25 }}>
                      {timeAgo(alert.created_at)}
                    </Typography>
                  </Box>
                </React.Fragment>
              );
            })
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
